// src/components/doctor-profile/ReviewsSection.jsx
import React, { useMemo, useState } from "react";
import { Star, Calendar, ThumbsUp } from "lucide-react";
import { styles as baseStyles } from "../../style/DoctorProfile.styles";

const ReviewsSection = ({ reviews = [], reviewsLoading, reviewStats, styles = baseStyles }) => {
  const brand = {
    primary: styles?.brandPrimary || "#0ea5e9",
    secondary: styles?.brandSecondary || "#6366f1",
    dark: styles?.brandDark || "#0f172a",
    amber: "#fbbf24",
  };

  const formatDate = (date) =>
    new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" }).format(date);

  const Stars = ({ rating = 0, size = 14 }) => (
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          style={{
            width: size,
            height: size,
            color: i <= rating ? brand.amber : "#e5e7eb",
            fill: i <= rating ? brand.amber : "transparent",
          }}
        />
      ))}
    </div>
  );

  const RatingBar = ({ stars, count, total }) => {
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (
      <div className="rv-bar">
        <span className="rv-bar-stars">{stars}</span>
        <Star style={{ width: 10, height: 10, color: brand.amber, fill: brand.amber }} />
        <div className="rv-bar-track">
          <div className="rv-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="rv-bar-count">{count}</span>
      </div>
    );
  };

  const sortedReviews = useMemo(() => {
    const arr = Array.isArray(reviews) ? [...reviews] : [];
    return arr.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
  }, [reviews]);

  const INITIAL_COUNT = 3;
  const [shown, setShown] = useState(INITIAL_COUNT);
  const total = reviewStats?.totalReviews || sortedReviews.length || 0;
  const visible = sortedReviews.slice(0, shown);
  const canShowMore = shown < sortedReviews.length;

  return (
    <div className="rv-wrap">
      <style>{`
        :root{
          --rv-primary:${brand.primary};
          --rv-secondary:${brand.secondary};
          --rv-dark:${brand.dark};
        }
        .rv-wrap{
          border-radius:20px;
          padding:8px;
          background:
            radial-gradient(700px 240px at 50% -20%, var(--rv-primary)0f, transparent 65%),
            radial-gradient(500px 200px at 90% 0%, var(--rv-secondary)0d, transparent 55%);
        }
        .rv-card{
          background:rgba(255,255,255,0.9);
          border:1px solid rgba(255,255,255,0.55);
          backdrop-filter:blur(8px);
          -webkit-backdrop-filter:blur(8px);
          border-radius:16px;
          padding:12px;
          box-shadow:0 18px 40px -18px rgba(2,6,23,0.25);
        }

        /* Header */
        .rv-head{ display:flex; align-items:center; gap:8px; margin-bottom:8px; }
        .rv-title{
          margin:0; font-weight:800; color:var(--rv-dark); letter-spacing:-0.01em;
          font-size:16px;
        }

        /* Overview */
        .rv-overview{
          display:grid; grid-template-columns:1fr; gap:10px; margin-bottom:12px;
        }
        .rv-avg{ text-align:center; }
        .rv-avg-num{ font-size:28px; font-weight:800; color:var(--rv-dark); margin-bottom:4px; }
        .rv-avg-sub{ font-size:12px; color:#6b7280; }

        .rv-bars{ padding:0 4px; }
        .rv-bar{
          display:flex; align-items:center; gap:8px; margin-bottom:6px;
        }
        .rv-bar-stars{ font-size:12px; font-weight:700; color:#374151; min-width:18px; }
        .rv-bar-track{ flex:1; height:6px; background:#f3f4f6; border-radius:3px; overflow:hidden; }
        .rv-bar-fill{ height:100%; background:#fbbf24; transition:width .25s ease; }
        .rv-bar-count{ font-size:11px; color:#6b7280; min-width:24px; text-align:right; }

        /* List */
        .rv-list{ display:flex; flex-direction:column; gap:10px; }
        .rv-item{
          background:#ffffff; border:1px solid #e2e8f0; border-radius:12px; padding:12px;
        }
        .rv-item-head{
          display:flex; justify-content:space-between; align-items:flex-start; gap:8px; margin-bottom:8px;
        }
        .rv-patient{ display:flex; align-items:center; gap:10px; min-width:0; }
        .rv-avatar{
          width:32px; height:32px; border-radius:999px; background:#3b82f6; color:#fff;
          display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; flex:0 0 auto;
        }
        .rv-name{ font-size:13px; font-weight:800; color:var(--rv-dark); margin-bottom:2px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .rv-date{ font-size:11px; color:#6b7280; }

        .rv-item-rating{ display:flex; align-items:center; gap:6px; flex-shrink:0; }
        .rv-item-score{ font-size:12px; font-weight:800; color:var(--rv-dark); }

        .rv-body{ font-size:13px; color:#374151; line-height:1.5; margin-bottom:8px; }
        .rv-meta{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; font-size:11px; color:#6b7280; }
        .rv-pill{
          background:#dbeafe; color:#1e40af; padding:3px 6px; border-radius:10px; font-size:10px; font-weight:700;
          display:inline-flex; align-items:center; gap:4px;
        }
        .rv-reco{ display:inline-flex; align-items:center; gap:4px; color:#059669; font-weight:700; }

        /* Footer buttons */
        .rv-footer{ display:flex; justify-content:center; margin-top:12px; }
        .rv-btn{
          min-height:36px; padding:8px 12px; border-radius:10px; font-weight:800; font-size:12px;
          display:inline-flex; align-items:center; justify-content:center; gap:8px; touch-action:manipulation;
        }
        .rv-btn--primary{ background: linear-gradient(135deg, var(--rv-primary), var(--rv-secondary));
color: #fff; border: none;
 }
        .rv-btn--ghost{ background:#fff; color:#111827; border:1px solid #E5E7EB; }

        /* Loading / Empty */
        .rv-state{ text-align:center; padding:28px 12px; }
        .rv-state-sub{ color:#6b7280; margin-top:8px; font-size:12px; }

        /* Upscale on wide screens */
        @media (min-width:900px){
          .rv-card{ padding:16px; }
          .rv-title{ font-size:18px; }
          .rv-overview{ grid-template-columns:1fr 2fr; gap:18px; margin-bottom:16px; }
          .rv-avg-num{ font-size:36px; }
        }
      `}</style>

      <div className="rv-card">
        {/* Header */}
        <div className="rv-head">
          <Star style={{ width: 16, height: 16, color: "#f59e0b" }} />
          <h2 className="rv-title">Patient Reviews ({total})</h2>
        </div>

        {/* Loading */}
        {reviewsLoading && (
          <div className="rv-state">
            <div style={{ ...styles.spinner, width: 18, height: 18, margin: "0 auto" }} />
            <p className="rv-state-sub">Loading reviews...</p>
          </div>
        )}

        {/* Empty */}
        {!reviewsLoading && total === 0 && (
          <div className="rv-state" style={{ color: "#9ca3af" }}>
            <Star style={{ width: 28, height: 28, margin: "0 auto 8px auto" }} />
            <h3 style={{ fontSize: 14, fontWeight: 800, color: brand.dark, marginBottom: 4 }}>No reviews yet</h3>
            <p style={{ margin: 0, fontSize: 12 }}>Be the first to leave a review</p>
          </div>
        )}

        {/* Content */}
        {!reviewsLoading && total > 0 && (
          <>
            {/* Overview */}
            <div className="rv-overview">
              <div className="rv-avg">
                <div className="rv-avg-num">{Number(reviewStats.averageRating).toFixed(1)}</div>
                <div style={{ marginBottom: 6, display: "flex", justifyContent: "center" }}>
                  <Stars rating={reviewStats.averageRating} size={16} />
                </div>
                <div className="rv-avg-sub">
                  Based on {total} review{total !== 1 ? "s" : ""}
                </div>
              </div>

              
            </div>

            {/* List */}
            <div className="rv-list">
              {visible.map((review) => (
                <div key={review.id} className="rv-item">
                  <div className="rv-item-head">
                    <div className="rv-patient">
                      <div className="rv-avatar">
                        {(review.patientInfo?.name || "Anonymous").charAt(0).toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="rv-name">
                          {review.patientInfo?.name || "Anonymous Patient"}
                        </div>
                        <div className="rv-date">{formatDate(review.createdAt)}</div>
                      </div>
                    </div>

                    <div className="rv-item-rating">
                      <Stars rating={review.rating} size={12} />
                      <span className="rv-item-score">
                        {Number(review.rating || 0).toFixed(1)}
                      </span>
                    </div>
                  </div>

                  <div className="rv-body">“{review.comment}”</div>

                  <div className="rv-meta">
                    {review.serviceName && (
                      <span className="rv-pill">
                        <Calendar style={{ width: 10, height: 10 }} />
                        {review.serviceName}
                      </span>
                    )}
                    {review.wouldRecommend && (
                      <span className="rv-reco">
                        <ThumbsUp style={{ width: 10, height: 10 }} />
                        Recommends
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Show more / less */}
            {sortedReviews.length > INITIAL_COUNT && (
              <div className="rv-footer">
                {!canShowMore ? (
                  <button className="rv-btn rv-btn--ghost" onClick={() => setShown(INITIAL_COUNT)}>
                    Show less
                  </button>
                ) : (
                  <button
                    className="rv-btn rv-btn--primary"
                    onClick={() => setShown((n) => Math.min(n + 5, sortedReviews.length))}
                  >
                    Show more reviews
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReviewsSection;
